import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, AlertCircle, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/accept-invite/${token}`);
        setInfo(r.data);
      } catch (err) {
        setError(err.response?.data?.detail || "This invite link is no longer valid.");
      }
      setLoading(false);
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw1.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (pw1 !== pw2) { setError("Passwords do not match."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/accept-invite/${token}`, { password: pw1 });
      setDone(true);
      setTimeout(() => navigate("/admin/login"), 2200);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not set password");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#0A1628] flex items-center justify-center px-4 py-12" data-testid="accept-invite-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-outfit font-bold text-3xl text-white tracking-tight">wedowedo</h1>
          <p className="text-xs font-mono text-[#857973] tracking-[0.2em] mt-1">BOOKKEEPING ACCESS</p>
        </div>

        <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-7 shadow-[0_0_40px_rgba(245,144,115,0.05)]">
          {loading && (
            <div className="flex flex-col items-center gap-2 py-8">
              <Loader2 className="w-6 h-6 text-[#F59073] animate-spin" />
              <span className="text-xs font-mono text-[#857973]">VALIDATING INVITE…</span>
            </div>
          )}

          {!loading && error && !info && (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <AlertCircle className="w-10 h-10 text-red-400" />
              <p className="text-white font-medium">Invite invalid</p>
              <p className="text-sm text-[#857973]">{error}</p>
              <p className="text-xs text-[#857973] mt-2">Ask your admin to send you a new invite.</p>
            </div>
          )}

          {!loading && info && !done && (
            <>
              <h2 className="font-outfit text-xl text-white mb-1">Welcome, {info.name?.split(" ")[0] || "there"}</h2>
              <p className="text-sm text-[#857973] mb-5">Set a password for <span className="text-[#F59073] font-mono">{info.email}</span> to access the bookkeeping portal.</p>

              <form onSubmit={submit} className="space-y-3">
                <div>
                  <label className="text-[10px] font-mono text-[#857973] tracking-[0.15em] block mb-1">PASSWORD</label>
                  <div className="relative">
                    <input value={pw1} onChange={e => setPw1(e.target.value)} type={showPw ? "text" : "password"} required minLength={8}
                      data-testid="invite-pw1" autoFocus
                      className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2.5 rounded-sm text-sm focus:border-[#F59073] outline-none pr-10" />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#857973]">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-mono text-[#857973] tracking-[0.15em] block mb-1">CONFIRM PASSWORD</label>
                  <input value={pw2} onChange={e => setPw2(e.target.value)} type={showPw ? "text" : "password"} required minLength={8}
                    data-testid="invite-pw2"
                    className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2.5 rounded-sm text-sm focus:border-[#F59073] outline-none" />
                </div>

                {error && (
                  <p className="text-xs text-red-400 flex items-center gap-1.5"><AlertCircle className="w-3 h-3" />{error}</p>
                )}

                <button type="submit" disabled={submitting} data-testid="invite-submit"
                  className="w-full bg-[#F59073] text-black py-2.5 rounded-sm text-sm font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {submitting ? "Saving…" : "Set password & continue"}
                </button>
              </form>
            </>
          )}

          {!loading && done && (
            <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="invite-success">
              <CheckCircle2 className="w-12 h-12 text-green-400" />
              <p className="text-white font-medium">Password set</p>
              <p className="text-sm text-[#857973]">Redirecting you to login…</p>
              <Link to="/admin/login" className="text-[#F59073] text-xs underline mt-2">Or click here</Link>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-[#857973] mt-6">
          <Link to="/admin/login" className="hover:text-[#F59073]">Already set up? Go to login →</Link>
        </p>
      </div>
    </div>
  );
}
